import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import HttpManager from "../../../services/HttpManager";
import { ProspectEvent, ProspectRow } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { errorTexto, iniciales, nombreCompleto, tonoAvatar } from "./salesHelpers";
import "./equipoReenviar.css";

const MAX = 10;

/** Texto de un mensaje de WhatsApp sin el relleno "[Foto]", "[Nota de voz]"… */
export const textoDe = (ev: ProspectEvent): string => (ev.body && !/^\[[^\]]*\]$/.test(ev.body.trim()) ? ev.body : "");

const kindDe = (ev: ProspectEvent): "voice" | "media" | "document" => {
	const t = String(ev.meta?.type || "");
	const mime = String(ev.meta?.mime || "");
	if (t === "audio" || mime.startsWith("audio/")) return "voice";
	if (t === "image" || t === "video" || t === "sticker" || mime.startsWith("image/") || mime.startsWith("video/")) return "media";
	return "document";
};

const nombreArchivo = (ev: ProspectEvent): string => {
	if (ev.meta?.filename) return String(ev.meta.filename);
	const ext = String(ev.meta?.mime || "").split("/")[1]?.split(";")[0] || "bin";
	return "archivo-" + ev.id + "." + ext.replace("jpeg", "jpg").replace("quicktime", "mov");
};

interface Props {
	campusId: number;
	/** La conversación de donde salen los mensajes (para bajar sus archivos). */
	prospectId: number;
	/** En orden, del más viejo al más nuevo. */
	eventos: ProspectEvent[];
	onCerrar: () => void;
	onEnviado?: () => void;
}

/**
 * Reenviar mensajes de WappChat a otros clientes (Esteban, 2026-10-12): salen como mensajes NUEVOS (sin "Reenviado") y con
 * las reglas de siempre: el servidor elige el número y, con la ventana de 24 h cerrada, no deja mandar (se dice por cliente).
 */
const ReenviarClientes: React.FC<Props> = ({ campusId, prospectId, eventos, onCerrar, onEnviado }) => {
	const http = HttpManager.getInstance();
	const [q, setQ] = useState("");
	const [rows, setRows] = useState<ProspectRow[] | null>(null);
	const [elegidos, setElegidos] = useState<ProspectRow[]>([]);
	const [busy, setBusy] = useState(false);
	const [progreso, setProgreso] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const t = setTimeout(() => {
			const f: any = q.trim() ? { owner: "all", q: q.trim(), lite: 1, sinTotal: 1 } : { owner: "all", lite: 1, sinTotal: 1 };
			http.prospectsService.list(campusId, f, 30, 1).then((r) => setRows(r.data.data.filter((x) => x.id !== prospectId)))
				.catch((e) => { setRows([]); setError(errorTexto(e)); });
		}, q ? 300 : 0);
		return () => clearTimeout(t);
	}, [q, campusId]);
	useEffect(() => {
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onCerrar(); };
		window.addEventListener("keydown", esc);
		return () => window.removeEventListener("keydown", esc);
	}, [onCerrar, busy]);

	const lleno = elegidos.length >= MAX;
	const toggle = (r: ProspectRow) => setElegidos((prev) => prev.some((x) => x.id === r.id) ? prev.filter((x) => x.id !== r.id) : lleno ? prev : [...prev, r]);

	const enviar = async () => {
		if (!elegidos.length || busy) return;
		setBusy(true); setError(null);
		const fallas: string[] = [];
		const blobs = new Map<number, Blob>();
		let listos = 0;
		for (const d of elegidos) {
			const quien = nombreCompleto(d);
			setProgreso("Enviando a " + quien.split(" ")[0] + "…");
			try {
				for (const ev of eventos) {
					if (ev.meta?.file) {
						let b = blobs.get(ev.id);
						if (!b) { b = (await http.prospectsService.mediaArchivo(campusId, prospectId, ev.id)).data; blobs.set(ev.id, b); }
						const k = kindDe(ev);
						await http.prospectsService.sendWhatsappMedia(campusId, d.id, b, nombreArchivo(ev), k, { caption: k !== "voice" ? textoDe(ev) || undefined : undefined });
					} else if (textoDe(ev)) {
						await http.prospectsService.sendWhatsapp(campusId, d.id, { text: textoDe(ev) });
					}
				}
				listos++;
			} catch (e) {
				fallas.push(quien + ": " + errorTexto(e));
			}
		}
		setBusy(false); setProgreso(null);
		if (listos && onEnviado) onEnviado();
		if (fallas.length) {
			setError((listos ? "Se mandó a " + listos + (listos === 1 ? " cliente" : " clientes") + ". No se pudo con: " : "") + fallas.join(" · "));
			setElegidos([]);
			return;
		}
		onCerrar();
	};

	const n = eventos.length;
	return createPortal(
		<div className="eqr-velo" onClick={busy ? undefined : onCerrar}>
			<div className="eqr" role="dialog" aria-modal="true" aria-label="Reenviar a Clientes" onClick={(e) => e.stopPropagation()}>
				<div className="eqr-cab">
					<b>{n > 1 ? "Reenviar " + n + " Mensajes" : "Reenviar Mensaje"}</b>
					<button className="eqr-x" onClick={onCerrar} disabled={busy} aria-label="Cerrar"><WappIcono n="x" /></button>
				</div>
				<div className="eqr-vista"><WappIcono n="forward" /><span>{n === 1 ? (textoDe(eventos[0]) || "Archivo") : n + " mensajes"} · salen como mensajes nuevos</span></div>
				<input className="eqr-buscar" type="search" autoFocus placeholder="Buscar cliente por nombre o teléfono" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar cliente" />
				<div className="eqr-lista">
					{rows === null ? <div className="eqr-vacio">Cargando…</div> : null}
					{rows && rows.length ? <div className="eqr-sec">{q.trim() ? "Clientes" : "Conversaciones recientes"}</div> : null}
					{(rows || []).map((r) => {
						const on = elegidos.some((x) => x.id === r.id);
						return (
							<button key={r.id} type="button" className={"eqr-fila" + (on ? " on" : "")} disabled={(!on && lleno) || busy} aria-pressed={on} onClick={() => toggle(r)}>
								<span className={"eqr-av " + tonoAvatar(r.id)} aria-hidden="true">{iniciales(r)}</span>
								<span className="eqr-nom"><b>{nombreCompleto(r)}</b>{r.phone_e164 ? <small>{telefonoBonito(r.phone_e164)}</small> : null}</span>
								<span className={"eqr-marca" + (on ? " on" : "")} aria-hidden="true">{on ? <WappIcono n="check" /> : null}</span>
							</button>
						);
					})}
					{rows && !rows.length ? <div className="eqr-vacio">No hay resultados.</div> : null}
				</div>
				{error ? <div className="eqr-error" role="alert">{error}</div> : null}
				<div className="eqr-pie">
					<span className="eqr-elegidos">{progreso || (elegidos.length ? elegidos.map((x) => nombreCompleto(x).split(" ")[0]).join(", ") : "Elige hasta " + MAX + " clientes")}
						{lleno && !progreso ? <small> (máximo {MAX})</small> : null}</span>
					<button className="eqr-enviar" disabled={!elegidos.length || busy} onClick={enviar}>{busy ? "Enviando…" : "Enviar"}{elegidos.length && !busy ? " (" + elegidos.length + ")" : ""}</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

export default ReenviarClientes;
